/**
 * The user's default check BACK arrangement (Phase: saveable backs).
 *
 * When Princewill arranges the back of a check - drags its objects
 * into place, picks the deposit style - he can save that arrangement
 * as his default so new checks and design switches open with it
 * instead of making him rearrange it every time. This module is the
 * storage model: a versioned localStorage envelope plus the pure
 * position-map operations used to apply / clear the default against
 * the studio's shared objectPositions state.
 *
 * The arrangement is USER DATA captured from the live back canvas
 * (object ids are the CheckBackEngine's stable ids); nothing here
 * changes the shipped endorsement geometry.
 */

export interface BackArrangement {
    left: number;
    top: number;
    scaleX: number;
    scaleY: number;
    angle: number;
}

export interface BackDefault {
    /** Deposit-block style id (BACK_SIDE_STYLES) in effect when saved. */
    style: string;
    /** Back-object positions captured from the live back canvas. */
    positions: Record<string, BackArrangement>;
    /** Epoch ms of the save; newest wins between device and account. */
    savedAt: number;
}

export const BACK_DEFAULT_KEY = 'oc-check-back-default:v1';

/**
 * Object ids from retired back compositions that must never ride
 * back into state from a saved arrangement: 'chem-wash-detection'
 * was the standalone open-field Chemical Wash Detection group of
 * an earlier composition (the box lives INSIDE the security panel
 * now, composed by the engine on every render). A positions entry
 * for it is inert — the engine never creates that object — but a
 * stale entry is still user-state pollution, so every load path
 * strips it.
 */
export const LEGACY_BACK_OBJECT_IDS = ['chem-wash-detection'];

/** Copy a positions map with retired-object entries removed. */
export function stripLegacyBackObjectPositions<T extends Record<string, any>>(positions: T): T {
    if (!positions || typeof positions !== 'object') return positions;
    if (!LEGACY_BACK_OBJECT_IDS.some(id => id in positions)) return positions;
    const out: Record<string, any> = { ...positions };
    for (const id of LEGACY_BACK_OBJECT_IDS) delete out[id];
    return out as T;
}

/**
 * THE MOBILE-DEPOSIT BLOCK GROUP (2026-11-04): the tick box
 * ('mobile-checkbox') and its "CHECK HERE IF MOBILE DEPOSIT" label
 * ('mobile-instruction') are ONE fabric Group ('mobile-deposit-block')
 * in the engine now — as siblings, dragging or enlarging the box left
 * the label behind at its original seat and size, defeating the
 * owner's purpose in enlarging the block at all (the label text was
 * the part he found too small). The arrangement system records a
 * single transform for the whole block under the group id.
 *
 * Arrangements saved while the pieces were siblings carry entries
 * for the two piece ids and none for the group. Those entries are
 * USER DATA and must not be orphaned: the lookup heal below
 * synthesizes the group's entry from them, the same pattern as the
 * security panel's frame fallback (see backPositionFor in
 * CheckBuilder), extended with the anchor arithmetic the panel did
 * not need (the panel's frame entry WAS its group anchor; the mobile
 * pieces record CENTRE-origin transforms, the group records its
 * bounding-box top-left).
 *
 * The arithmetic, from the engine's construction at the studio
 * scale (scaleFactor 72 — every live build/export path): the group
 * anchor is the union of the parts' bounding rects. The box heads
 * the union: it is 16 wide with a 1.5 stroke, so its bounding half
 * extent is (16 + 1.5) / 2 = 8.75, and it is wider than the rotated
 * label and stands above the label's top — so the box's centre sits
 * exactly (8.75, 8.75) right/down of the anchor, at any back width
 * or height. The label shares the box's column (same centre x, the
 * same 8.75 from the anchor) and its centre stands at
 * height * mobileTextCenterYRatio (0.52) while the anchor's top is
 * the box centre (mobileBoxCenterY 104) minus 8.75.
 *
 * Which piece anchors the heal when both entries exist? THE BOX:
 * it is the piece the owner grabs, and if he enlarged the box alone
 * (the old defect), the unified block inherits that enlargement —
 * label included — which is what he wanted all along. The group's
 * angle is always 0: the pieces carry their own specimen +90 inside
 * the group, and a stale pair rotation is not arrangement data the
 * product keeps (the label's direction is the specimen invariant
 * healed by normalizeBackTextDirections).
 */
export const MOBILE_DEPOSIT_BLOCK_ID = 'mobile-deposit-block';
export const MOBILE_CHECKBOX_ID = 'mobile-checkbox';
export const MOBILE_INSTRUCTION_ID = 'mobile-instruction';

/** The box centre's offset from the block group's anchor (see above). */
const MOBILE_BLOCK_BOX_ANCHOR_OFFSET = 8.75;
/** The box centre's natural seat from the back's top (engine geometry). */
const MOBILE_BLOCK_BOX_CENTER_Y = 104;
/** The label centre's natural seat as a ratio of the back's height. */
const MOBILE_BLOCK_LABEL_CENTER_Y_RATIO = 0.52;

/**
 * Synthesize the mobile-deposit BLOCK's arrangement entry from a
 * legacy per-piece entry, or return undefined when the map holds
 * neither piece. `backHeight` is the height of the back being built
 * (the label-anchored fallback needs it; the box-anchored path —
 * the one real saved maps take, since a harvest records every
 * object together — does not). Pure: the input map is never mutated.
 */
export function mobileDepositBlockEntryFromLegacy(
    positions: Record<string, any> | null | undefined,
    backHeight: number = 350
): BackArrangement | undefined {
    if (!positions || typeof positions !== 'object') return undefined;
    const box = positions[MOBILE_CHECKBOX_ID];
    if (box && typeof box.left === 'number' && typeof box.top === 'number') {
        const sx = num(box.scaleX, 1);
        const sy = num(box.scaleY, 1);
        return {
            left: box.left - (MOBILE_BLOCK_BOX_ANCHOR_OFFSET * sx),
            top: box.top - (MOBILE_BLOCK_BOX_ANCHOR_OFFSET * sy),
            scaleX: sx,
            scaleY: sy,
            angle: 0
        };
    }
    const label = positions[MOBILE_INSTRUCTION_ID];
    if (label && typeof label.left === 'number' && typeof label.top === 'number') {
        const sx = num(label.scaleX, 1);
        const sy = num(label.scaleY, 1);
        const labelCenterBelowAnchor =
            (backHeight * MOBILE_BLOCK_LABEL_CENTER_Y_RATIO) - (MOBILE_BLOCK_BOX_CENTER_Y - MOBILE_BLOCK_BOX_ANCHOR_OFFSET);
        return {
            left: label.left - (MOBILE_BLOCK_BOX_ANCHOR_OFFSET * sx),
            top: label.top - (labelCenterBelowAnchor * sy),
            scaleX: sx,
            scaleY: sy,
            angle: 0
        };
    }
    return undefined;
}

/**
 * SPECIMEN TEXT DIRECTION (owner's governing back standard,
 * 2026-11-04, photo-proven by crop-and-rotate against his specimen
 * back image-33.png): EVERY rotated text on the back of a check
 * reads in the specimen's direction — fabric angle +90, letter-tops
 * pointing right, lines reading top-to-bottom. Nothing on a
 * specimen back uses -90; the owner rejects that direction outright
 * ("facing down" — a bank would refuse the back).
 *
 * The engine builds every one of these texts at +90. But saved
 * arrangements are USER STATE captured from the live canvas, and
 * for a window on 2026-11-04 the shipped panel composition stood
 * at -90 (commit e2f1fbd1, corrected by 9d4de753 the same day;
 * RESERVED's own -90 detour ran until d926e360). Any default back,
 * saved design or auto-persisted working state captured in those
 * windows carries angle -90 for the texts below, and the studio
 * stamps saved angles over the fresh engine objects on every
 * rebuild — so the stale direction re-imposed itself forever and
 * re-saved itself on every harvest. These helpers are the heal:
 * the direction of a specimen text is a product invariant, not
 * arrangement data. POSITIONS and scales in a saved entry are the
 * user's arrangement and are preserved exactly; only the poisoned
 * angle value (-90, or its 270 spelling) is rewritten to +90, and
 * only for the ids below.
 */
export const SPECIMEN_BACK_TEXT_ANGLE = 90;

export const SPECIMEN_BACK_TEXT_IDS: readonly string[] = [
    'security-regcc',        // Reg CC footnote on the far-left edge
    'security-header',       // SECURITY FEATURES panel title (panel-group child)
    'chem-wash-label',       // CHEMICAL WASH DETECTION label (panel-group child)
    'echeck-instructions',   // printed-eCheck instructions box rows
    'echeck-instructions-2',
    'echeck-instructions-3',
    'endorse-header',        // ENDORSE HERE
    'endorse-x',
    'endorse-line-0',        // Treasury variant: the line itself as microtext
    'endorse-warning',       // DO NOT WRITE, SIGN OR STAMP BELOW THIS LINE
    'endorse-reserved',      // RESERVED FOR FINANCIAL INSTITUTION USE*
    'strip-note',            // variant note in the reserved seat
    'mobile-instruction',    // CHECK HERE IF MOBILE DEPOSIT
    'deposit-only-name',
    'deposit-only-label',    // FOR DEPOSIT ONLY
    'deposit-only-account',
    'back-imagematch'
];

/** True for the back texts whose direction the specimen fixes at +90. */
export function isSpecimenBackText(id: string | null | undefined): boolean {
    if (!id) return false;
    // The panel's feature lines carry generated ids (security-feature-0…).
    return SPECIMEN_BACK_TEXT_IDS.includes(id) || id.startsWith('security-feature-');
}

/**
 * The angle a saved position may impose on an object: for a
 * specimen text the saved angle NEVER wins — the direction is the
 * engine's +90 invariant — while every other object keeps its saved
 * angle exactly (live arrangements round-trip untouched).
 */
export function specimenAngleFor(id: string | null | undefined, savedAngle: number | undefined): number | undefined {
    return isSpecimenBackText(id) ? SPECIMEN_BACK_TEXT_ANGLE : savedAngle;
}

/**
 * Copy a positions map with stale specimen directions healed:
 * entries for specimen texts whose saved angle is the backwards
 * value (-90, spelled -90 or 270 after a fabric round-trip) are
 * rewritten to +90. Left/top/scale and every other object's entry
 * pass through untouched; the input is never mutated, and the
 * input itself is returned when nothing needs healing.
 */
export function normalizeBackTextDirections<T extends Record<string, any>>(positions: T): T {
    if (!positions || typeof positions !== 'object') return positions;
    let out: Record<string, any> | null = null;
    for (const [id, entry] of Object.entries(positions)) {
        if (!isSpecimenBackText(id) || !entry || typeof entry !== 'object') continue;
        const a = entry.angle;
        if (typeof a !== 'number' || !Number.isFinite(a)) continue;
        const norm = ((a % 360) + 360) % 360;
        if (norm === 270) {
            if (!out) out = { ...positions };
            out[id] = { ...entry, angle: SPECIMEN_BACK_TEXT_ANGLE };
        }
    }
    return (out ?? positions) as T;
}

const num = (v: unknown, fallback: number): number =>
    (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

function normalizeArrangement(raw: any): BackArrangement | null {
    if (!raw || typeof raw !== 'object') return null;
    if (typeof raw.left !== 'number' || typeof raw.top !== 'number') return null;
    return {
        left: raw.left,
        top: raw.top,
        scaleX: num(raw.scaleX, 1),
        scaleY: num(raw.scaleY, 1),
        angle: num(raw.angle, 0)
    };
}

export function serializeBackDefault(d: BackDefault): string {
    // Envelope v2 (2026-11-04): written by the specimen-pattern
    // build. v1 envelopes load identically (see
    // normalizeBackDefault) — the version marks the pattern era
    // for any future migration that needs it.
    return JSON.stringify({ v: 2, style: d.style, positions: d.positions, savedAt: d.savedAt });
}

/**
 * Validate/normalize a default-back value (object form - e.g. an
 * account design's data). Returns null for anything that is not a
 * well-formed shape, so a legacy or corrupt value can never reach
 * the canvas.
 *
 * Version handling: v2 is the current envelope; v1 (and the
 * unversioned values that predate the field) load identically.
 * The one cleanup at every version: entries for retired back
 * objects (LEGACY_BACK_OBJECT_IDS — the standalone wash group)
 * are stripped, so the legacy field box can never re-enter state
 * from a saved default.
 *
 * Panel entries are DELIBERATELY preserved across the
 * 2026-11-04 specimen-pattern change: the panel is rebuilt from
 * the engine on every render (no saved composition can survive as
 * a remnant), and the panel group's arrangement anchor — its
 * frame's top-left corner — is unchanged by the pattern (the
 * group is constructed at the frame and, with the footnote now
 * standing outside it, its bounds are exactly the frame's). A v1
 * entry therefore lands the NEW composition in exactly the seat
 * the user saved, and an untouched panel entry lands on the
 * default seat it always had. Dropping the entries would delete
 * the user's saved arrangement to fix a misplacement that cannot
 * occur.
 */
export function normalizeBackDefault(parsed: any): BackDefault | null {
    if (!parsed || typeof parsed !== 'object') return null;
    const version = parsed.v === undefined ? 1 : parsed.v;
    if (version !== 1 && version !== 2) return null;
    if (typeof parsed.style !== 'string' || !parsed.style) return null;
    if (!parsed.positions || typeof parsed.positions !== 'object') return null;
    const positions: Record<string, BackArrangement> = {};
    for (const [id, value] of Object.entries(parsed.positions)) {
        if (LEGACY_BACK_OBJECT_IDS.includes(id)) continue;
        const arr = normalizeArrangement(value);
        if (arr) positions[id] = arr;
    }
    if (Object.keys(positions).length === 0) return null;
    // Positions and scales land exactly as saved (see the panel
    // note above); the ONE rewrite is the specimen-direction heal —
    // a default captured while the backwards (-90) composition was
    // live must not re-impose that direction on the corrected back.
    return { style: parsed.style, positions: normalizeBackTextDirections(positions), savedAt: num(parsed.savedAt, 0) };
}

/** Parse a stored default-back envelope (the localStorage form). */
export function parseBackDefault(raw: string | null): BackDefault | null {
    if (!raw) return null;
    try {
        return normalizeBackDefault(JSON.parse(raw));
    } catch {
        return null;
    }
}

/** Field-for-field comparison (missing scale/angle take their defaults). */
export function arrangementsEqual(a: Partial<BackArrangement> | undefined, b: Partial<BackArrangement> | undefined): boolean {
    if (!a || !b) return false;
    return num(a.left, 0) === num(b.left, 0)
        && num(a.top, 0) === num(b.top, 0)
        && num(a.scaleX, 1) === num(b.scaleX, 1)
        && num(a.scaleY, 1) === num(b.scaleY, 1)
        && num(a.angle, 0) === num(b.angle, 0);
}

/** Apply a saved arrangement over the current positions (default wins per object). */
export function mergeArrangementPositions(
    current: Record<string, BackArrangement>,
    positions: Record<string, BackArrangement>
): Record<string, BackArrangement> {
    const out: Record<string, BackArrangement> = { ...current };
    for (const [id, arr] of Object.entries(positions)) out[id] = { ...arr };
    return out;
}

/**
 * Undo an applied arrangement on clear: remove only the entries that
 * still hold the default's exact values. Entries the user moved
 * after the default was applied are his current design and stay.
 */
export function stripArrangementPositions(
    current: Record<string, BackArrangement>,
    positions: Record<string, BackArrangement>
): Record<string, BackArrangement> {
    const out: Record<string, BackArrangement> = { ...current };
    for (const [id, arr] of Object.entries(positions)) {
        if (arrangementsEqual(out[id], arr)) delete out[id];
    }
    return out;
}
